/**
 * Reportes → Historial de cambios de precios (`/reportes/historial-precios`).
 *
 * Puerto de `Reportes::historial_precios` + `Historial_precio_model`
 * (`historial_precios_productos`) + `views/menu/reportes/historial_precios.php`.
 *
 * La pantalla del panel anterior tenía: filtros (fechas, tipo de producto y
 * usuario) con *Aplicar* y *Limpiar*, cuatro tarjetas (**total de cambios**,
 * **aumentos**, **disminuciones** y **productos modificados**), la tabla
 * *Registro de cambios de precios* (fecha, hora, producto, tipo, precio anterior,
 * precio nuevo, diferencia, % de cambio, usuario y el badge del cambio) y los
 * botones de exportación (Excel, PDF, CSV e impresión).
 *
 * Reglas conservadas:
 * - Sin fechas, el rango por defecto son los **últimos 30 días** (el mismo del
 *   panel anterior). Como el histórico puede estar fuera de ese rango, la pantalla
 *   avisa con el rango que **sí** tiene datos y ofrece un botón para verlo.
 * - El cambio se clasifica por el precio nuevo frente al anterior: **AUMENTO**,
 *   **DISMINUCIÓN** o **SIN CAMBIO** (el badge del original).
 *
 * Diferencias anotadas: se añaden el filtro por **tipo de cambio**, la **búsqueda
 * libre** (producto, usuario o motivo) y la exportación en **CSV** servida por la
 * API (el original la armaba en el navegador con librerías); el resumen se calcula
 * con los mismos filtros que el listado.
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw, RotateCcw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { formatCurrency, formatDateValue, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useCatalogosHistorial,
  useHistorialPrecios,
  type CambioPrecio,
  type FiltrosHistorial,
} from '../reportes.api';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Fecha local `AAAA-MM-DD` (no usar `toISOString`, que cambia de día). */
function fechaLocal(fecha: Date): string {
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');

  return `${fecha.getFullYear()}-${mes}-${dia}`;
}

/** Rango por defecto: últimos 30 días, como el panel anterior. */
function rangoPorDefecto(): FiltrosHistorial {
  const hoy = new Date();
  const hace30 = new Date(hoy.getTime() - 30 * 24 * 60 * 60 * 1000);

  return {
    desde: fechaLocal(hace30),
    hasta: fechaLocal(hoy),
    tipo: '',
    usuario_id: null,
    cambio: '',
    busqueda: '',
  };
}

/** Tono del badge según el cambio, como en el panel anterior. */
function tonoDelCambio(cambio: CambioPrecio['cambio']): 'success' | 'danger' | 'neutral' {
  if (cambio === 'AUMENTO') return 'danger';
  if (cambio === 'DISMINUCIÓN') return 'success';

  return 'neutral';
}

export function HistorialPreciosPage() {
  useDocumentTitle('Reportes · Historial de precios');

  const [filtros, setFiltros] = useState<FiltrosHistorial>(rangoPorDefecto);
  const [aviso, setAviso] = useState<string | null>(null);

  const catalogos = useCatalogosHistorial();
  const consulta = useHistorialPrecios(filtros);
  const datos = consulta.data;
  const resumen = datos?.resumen;

  const rango = catalogos.data?.rango;
  const fueraDeRango = Boolean(
    resumen && resumen.total === 0 && rango?.desde && rango.hasta && rango.total > 0,
  );

  const cambios = useMemo(() => datos?.cambios ?? [], [datos]);
  // El registro se pagina en el navegador; las tarjetas y el pie siguen usando el listado
  // completo.
  const paginacion = usePaginacion(cambios, { clave: filtros });

  const cambiar = <C extends keyof FiltrosHistorial>(campo: C, valor: FiltrosHistorial[C]) =>
    setFiltros((previos) => ({ ...previos, [campo]: valor }));

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarHistorialPrecios(filtros), {
        nombre: `historial-precios-${filtros.desde}_${filtros.hasta}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el reporte.');
    }
  };

  /** Abre el PDF del reporte (mismo listado, para imprimir o archivar). */
  const exportarPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfHistorialPrecios(filtros));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
            to="/reportes"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Historial de cambios de precios</h1>
          <p className="mt-1 text-sm text-ink-500">
            Quién cambió el precio de qué producto, cuándo y en cuánto.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button

            variant="outline"

            size="sm"

            leftIcon={<FileText className="size-4" aria-hidden="true" />}

            onClick={() => void exportarPdf()}

          >

            PDF

          </Button>

          <Button
            variant="outline"
            size="sm"
            leftIcon={<Download className="size-4" aria-hidden="true" />}
            onClick={() => void exportar()}
          >
            Exportar
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<RotateCcw className="size-4" aria-hidden="true" />}
            onClick={() => setFiltros(rangoPorDefecto())}
          >
            Limpiar
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
            onClick={() => void consulta.refetch()}
          >
            Actualizar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <div className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Desde</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.desde}
                  className="w-full px-3 text-sm"
                  onChange={(evento) => cambiar('desde', evento.target.value)}
                />
              </span>
            </div>

            <div className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Hasta</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.hasta}
                  className="w-full px-3 text-sm"
                  onChange={(evento) => cambiar('hasta', evento.target.value)}
                />
              </span>
            </div>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Producto</span>
              <Select
                value={filtros.tipo}
                onChange={(evento) => cambiar('tipo', evento.target.value)}
                className={CONTROL}
              >
                <option value="">Todos los tipos</option>
                {(catalogos.data?.tipos ?? []).map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {tipo}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Usuario</span>
              <Select
                value={filtros.usuario_id === null ? '' : String(filtros.usuario_id)}
                onChange={(evento) =>
                  cambiar('usuario_id', evento.target.value ? Number(evento.target.value) : null)
                }
                className={CONTROL}
              >
                <option value="">Todos los usuarios</option>
                {(catalogos.data?.usuarios ?? []).map((usuario) => (
                  <option key={usuario.id} value={String(usuario.id)}>
                    {usuario.nombre} ({usuario.cambios})
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Tipo de cambio</span>
              <Select
                value={filtros.cambio}
                onChange={(evento) => cambiar('cambio', evento.target.value)}
                className={CONTROL}
              >
                <option value="">Todos los cambios</option>
                {(catalogos.data?.cambios ?? []).map((opcion) => (
                  <option key={opcion.valor} value={opcion.valor}>
                    {opcion.etiqueta}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Buscar</span>
              <span className="relative block">
                <Search
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
                  aria-hidden="true"
                />
                <input
                  className={`${CONTROL} pl-9`}
                  placeholder="Producto, usuario o motivo…"
                  value={filtros.busqueda}
                  onChange={(evento) => cambiar('busqueda', evento.target.value)}
                />
              </span>
            </label>
          </div>

          {aviso ? (
            <Alert variant="error" title="No se pudo exportar">
              {aviso}
            </Alert>
          ) : null}

          {fueraDeRango ? (
            <Alert variant="info" title="El rango elegido no tiene cambios">
              Este ejemplar tiene cambios registrados entre {formatDateValue(rango?.desde)} y{' '}
              {formatDateValue(rango?.hasta)} ({formatNumber(rango?.total ?? 0)} en total).
              <span className="mt-2 block">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setFiltros((previos) => ({
                      ...previos,
                      desde: rango?.desde ?? previos.desde,
                      hasta: rango?.hasta ?? previos.hasta,
                    }))
                  }
                >
                  Ver todo el histórico
                </Button>
              </span>
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      {resumen ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            {
              etiqueta: 'Total de cambios',
              valor: formatNumber(resumen.total),
              detalle: `${formatNumber(resumen.usuarios)} usuario(s)`,
            },
            {
              etiqueta: 'Aumentos',
              valor: formatNumber(resumen.aumentos),
              detalle: `Variación media ${formatNumber(resumen.variacion_media ?? 0)} %`,
            },
            {
              etiqueta: 'Disminuciones',
              valor: formatNumber(resumen.disminuciones),
              detalle: `Máxima ${formatNumber(resumen.variacion_maxima ?? 0)} %`,
            },
            {
              etiqueta: 'Productos modificados',
              valor: formatNumber(resumen.productos),
              detalle: `${formatNumber(resumen.sin_cambio)} sin cambio`,
            },
          ].map((tarjeta) => (
            <Card key={tarjeta.etiqueta}>
              <CardContent className="p-4">
                <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  {tarjeta.etiqueta}
                </p>
                <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
                <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-800">
              Registro de cambios de precios ({formatNumber(cambios.length)})
            </h2>
            <span className="text-xs text-ink-500">
              {formatDateValue(datos?.filtros.desde)} – {formatDateValue(datos?.filtros.hasta)}
            </span>
          </div>

          {consulta.isError ? (
            <Alert variant="error" title="No se pudo cargar el historial">
              {consulta.error instanceof ApiError
                ? consulta.error.message
                : 'Inténtalo de nuevo en unos segundos.'}
            </Alert>
          ) : null}

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {!consulta.isLoading ? (
            <>
            <div className="max-h-[60vh] overflow-auto rounded-card border border-ink-100">
              <table className="w-full max-lg:min-w-[49rem] table-fixed border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="w-[5.25rem] px-1.5 py-2">Fecha</th>
                    <th className="w-[8.5rem] px-1.5 py-2">Producto</th>
                    <th className="w-[5.25rem] px-1.5 py-2 text-right">Antes</th>
                    <th className="w-[6rem] px-1.5 py-2 text-right">Ahora</th>
                    <th className="w-[4.5rem] px-1.5 py-2 text-right">% var.</th>
                    <th className="w-[8rem] px-1.5 py-2">Usuario</th>
                    <th className="w-[7.5rem] px-1.5 py-2">Cambio</th>
                  </tr>
                </thead>
                <tbody>
                  {cambios.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={7} className="px-1.5 py-10 text-center text-sm text-ink-500">
                        No hay cambios de precio que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {paginacion.visibles.map((cambio) => {
                    const diferencia = cambio.diferencia ?? 0;
                    return (
                      <tr key={cambio.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                        <td className="px-1.5 py-2">
                          <p className="truncate font-semibold text-ink-900">
                            {formatDateValue(cambio.fecha)}
                          </p>
                          <p className="text-[11px] text-ink-500">{cambio.hora ?? ''}</p>
                        </td>
                        <td className="px-1.5 py-2">
                          <p className="truncate font-medium text-ink-800" title={cambio.producto}>
                            {cambio.producto}
                          </p>
                          <p className="truncate text-[11px] text-ink-500" title={cambio.tipo ?? ''}>
                            {cambio.tipo ?? ''}
                          </p>
                        </td>
                        <td className="px-1.5 py-2 text-right text-ink-600">
                          <p className="truncate">{formatCurrency(cambio.precio_anterior ?? 0)}</p>
                        </td>
                        <td className="px-1.5 py-2 text-right">
                          <p className="truncate font-semibold text-ink-900">
                            {formatCurrency(cambio.precio_nuevo ?? 0)}
                          </p>
                          <p
                            className={`text-[11px] font-semibold ${
                              diferencia > 0
                                ? 'text-rose-600'
                                : diferencia < 0
                                  ? 'text-emerald-700'
                                  : 'text-ink-500'
                            }`}
                            title="Diferencia contra el precio anterior"
                          >
                            {diferencia > 0 ? '+' : ''}
                            {formatCurrency(diferencia)}
                          </p>
                        </td>
                        <td className="px-1.5 py-2 text-right whitespace-nowrap">
                          {cambio.porcentaje_cambio === null
                            ? '—'
                            : `${formatNumber(cambio.porcentaje_cambio)} %`}
                        </td>
                        <td className="px-1.5 py-2">
                          <p className="truncate text-ink-700" title={cambio.usuario}>
                            {cambio.usuario}
                          </p>
                          {cambio.motivo ? (
                            <p className="truncate text-[11px] text-ink-500" title={cambio.motivo}>
                              {cambio.motivo}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-1.5 py-2">
                          <Badge
                            variant={tonoDelCambio(cambio.cambio)}
                            className="whitespace-nowrap text-[11px]"
                          >
                            {cambio.cambio}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Paginador {...paginacion} etiqueta="cambios" />
            </>
          ) : null}
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        El historial se llena solo cada vez que se cambia el precio de un producto desde Productos. La
        exportación trae el mismo listado en CSV con los totales del periodo.
      </p>
    </div>
  );
}
